import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@affdice/rules/password-limits';

import { kitText } from './messages.ts';

/**
 * What a new password is held to, with no UI attached.
 *
 * Its own module rather than a corner of `ui.tsx` for two reasons. The rule
 * is policy, not presentation — three consoles and the staff-invite form all
 * apply it. And a `.tsx` file cannot be imported by this package's test
 * runner, which strips types but does not compile JSX, so a rule living
 * beside a component is a rule that cannot be tested directly.
 */

/**
 * §3.7 / §43.8 — the length bounds api-core enforces, refusing anything
 * outside them with `WEAK_PASSWORD`. Read from `@affdice/rules`, the same
 * file the server's `checkPassword` reads, so the form and the server cannot
 * disagree on a number.
 *
 * The server also refuses a password on the common-password list, or one
 * that contains the account's email address or business name. The form does
 * not mirror those: the list is some 80 KB the consoles would ship for a
 * check the server makes anyway, and the refusal names its reason
 * (`details.reason`, translated by `explainFailure`). There is deliberately
 * no uppercase/digit/symbol requirement behind any of these forms.
 */
export { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@affdice/rules/password-limits';

/** The reasons api-core gives in `details.reason` beside `WEAK_PASSWORD`. */
const REFUSAL_KEYS = {
  TOO_SHORT: 'password.refused.TOO_SHORT',
  TOO_LONG: 'password.refused.TOO_LONG',
  COMMON: 'password.refused.COMMON',
  PERSONAL: 'password.refused.PERSONAL',
} as const;

/**
 * The reader's-language sentence for a `WEAK_PASSWORD` refusal, or null when
 * the reason is missing or one this console does not know (the server's own
 * English message is then the answer).
 */
export function passwordRefusalText(reason: unknown): string | null {
  if (typeof reason !== 'string' || !Object.hasOwn(REFUSAL_KEYS, reason)) return null;
  const key = REFUSAL_KEYS[reason as keyof typeof REFUSAL_KEYS];
  return kitText(key, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH });
}

export interface PasswordRule {
  readonly label: string;
  readonly met: boolean;
}

/**
 * The rules a new password is actually held to, and whether this one meets them.
 *
 * Exactly the two the form enforces — the server's length minimum, and the
 * confirmation matching. Deliberately not a composition checklist: nothing
 * behind the form requires an uppercase letter, a digit or a symbol, and
 * stating a rule that is not enforced is worse than stating none. A red cross
 * beside a rule the server would have accepted teaches people the form is
 * lying, and the next real refusal is the one they ignore.
 *
 * The "again" row stays absent until there is something to compare, so it
 * does not sit there as a failure before the second box has been touched.
 */
export function passwordRules(value: string, again: string): readonly PasswordRule[] {
  return [
    {
      label: kitText('password.rule.length', { n: PASSWORD_MIN_LENGTH }),
      met: value.length >= PASSWORD_MIN_LENGTH,
    },
    // Shown only once broken: a ceiling nobody near it needs to read about.
    ...(value.length > PASSWORD_MAX_LENGTH
      ? [{ label: kitText('password.rule.maxLength', { n: PASSWORD_MAX_LENGTH }), met: false }]
      : []),
    ...(again === ''
      ? []
      : [{ label: kitText('password.rule.match'), met: value === again }]),
  ];
}
